import { useEffect, useState, useRef } from 'react';

/**
 * Hook to detect when the virtual keyboard is open on mobile devices
 * Works by monitoring the Visual Viewport API for height changes
 */
export const useKeyboardVisible = (): boolean => {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  // Track viewport heights (ref because these don't affect rendering)
  const viewportHeights = useRef({ initial: 0, last: 0 });

  useEffect(() => {
    // Check if Visual Viewport API is supported (iOS Safari 13+, Chrome)
    if (!window.visualViewport) {
      return;
    }

    // Store the initial height
    const initialHeight = window.visualViewport.height;
    viewportHeights.current = { initial: initialHeight, last: initialHeight };

    const handleResize = () => {
      if (!window.visualViewport) return;

      const currentHeight = window.visualViewport.height;
      const { initial, last } = viewportHeights.current;
      const heightDifference = last - currentHeight;

      // If viewport shrunk by more than 150px, keyboard is likely open
      // This threshold helps avoid false positives from browser chrome changes
      if (heightDifference > 150) {
        setIsKeyboardVisible(true);
      }
      // If viewport grew back, keyboard is likely closed
      else if (currentHeight >= initial - 50) {
        setIsKeyboardVisible(false);
      }

      viewportHeights.current.last = currentHeight;
    };

    // Listen to viewport resize events
    window.visualViewport.addEventListener('resize', handleResize);
    window.visualViewport.addEventListener('scroll', handleResize);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, []);

  return isKeyboardVisible;
};
